import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock, UserPlus, Trash2, Users } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function TeamPanel({ onUpgrade }) {
  const { t } = useTranslation();
  const [team, setTeam] = useState([]);
  const [isOwner, setIsOwner] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", team_role: "staff" });

  const load = useCallback(() => {
    api.get("/loueur/team").then((r) => { setTeam(r.data.team); setIsOwner(r.data.is_owner); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/loueur/team", form);
      toast.success(t("team.added")); setForm({ name: "", email: "", password: "", team_role: "staff" }); load();
    } catch (err) {
      if (err.response?.status === 403) { onUpgrade?.(); }
      toast.error(formatError(err.response?.data?.detail));
    }
  };
  const remove = async (id) => { try { await api.delete(`/loueur/team/${id}`); load(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); } };

  return (
    <div data-testid="team-panel" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {isOwner ? (
        <form onSubmit={add} className="bg-white rounded-2xl shadow-card border border-border p-5 space-y-3 h-fit">
          <p className="font-heading font-bold text-ink flex items-center gap-2"><UserPlus className="w-5 h-5 text-canard" /> {t("team.invite")}</p>
          <Input data-testid="team-name" placeholder={t("team.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl bg-sand" required />
          <Input data-testid="team-email" type="email" placeholder={t("team.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl bg-sand" required />
          <Input data-testid="team-password" type="password" placeholder={t("team.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl bg-sand" required />
          <select data-testid="team-role" value={form.team_role} onChange={(e) => setForm({ ...form, team_role: e.target.value })} className="h-11 rounded-xl border border-border bg-sand px-3 text-sm font-bold w-full">
            <option value="manager">{t("team.manager")}</option>
            <option value="staff">{t("team.staff")}</option>
            <option value="viewer">{t("team.viewer")}</option>
          </select>
          <Button type="submit" data-testid="team-add" className="rounded-full bg-canard hover:bg-canard-dark font-bold w-full">{t("team.add")}</Button>
        </form>
      ) : (
        <div className="bg-sand rounded-2xl border border-border p-5 text-sm text-muted2 font-body h-fit"><Lock className="w-5 h-5 text-canard mb-2" />{t("team.notOwner")}</div>
      )}

      <div className="space-y-3">
        <p className="font-heading font-bold text-ink flex items-center gap-2"><Users className="w-5 h-5 text-canard" /> {t("team.members")} ({team.length})</p>
        {!team.length && <div className="text-center py-8 bg-white rounded-2xl border border-border text-muted2 font-body">{t("team.empty")}</div>}
        {team.map((m) => (
          <div key={m.id} data-testid={`team-member-${m.id}`} className="bg-white rounded-2xl border border-border p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-ink">{m.name} <span className="text-xs font-bold text-canard bg-canard/10 rounded-full px-2 py-0.5 ml-1">{t(`team.${m.team_role}`)}</span></p>
              <p className="text-xs text-muted2">{m.email}</p>
            </div>
            {isOwner && <Button size="sm" variant="outline" data-testid={`team-remove-${m.id}`} onClick={() => remove(m.id)} className="rounded-full h-8 text-destructive border-destructive/30"><Trash2 className="w-3.5 h-3.5" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
